import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {get, ref, serverTimestamp, update} from 'firebase/database';
import {updateProfile} from 'firebase/auth';
import {database} from '../services/firebase';
import {useAuth} from '../contexts/AuthContext';

export default function ProfilePage() {
    const {currentUser, logout} = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // États pour le formulaire
    const [displayName, setDisplayName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [userStats, setUserStats] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        async function fetchUserData() {
            if (!currentUser) {
                navigate('/login');
                return;
            }

            try {
                // Récupérer les données du profil utilisateur
                const userRef = ref(database, `users/${currentUser.uid}`);
                const userSnapshot = await get(userRef);

                if (userSnapshot.exists()) {
                    const userData = userSnapshot.val();
                    setDisplayName(userData.profile?.displayName || '');
                    setPhoneNumber(userData.profile?.phoneNumber || '');
                    setEmail(userData.profile?.email || currentUser.email || '');
                    setIsAdmin(userData.profile?.role === 'admin');

                    // Récupérer les statistiques du joueur
                    setUserStats({
                        totalGames: userData.gameHistory?.totalGames || 0,
                        totalScore: userData.gameHistory?.totalScore || 0,
                        lastPlayed: userData.gameHistory?.lastPlayed || null,
                        questionsAnswered: userData.trainingStats?.questionsAnswered || 0,
                        correctAnswers: userData.trainingStats?.correctAnswers || 0
                    });
                }

                setLoading(false);
            } catch (error) {
                console.error("Erreur lors de la récupération des données utilisateur:", error);
                setError("Une erreur s'est produite lors du chargement de votre profil.");
                setLoading(false);
            }
        }

        fetchUserData();
    }, [currentUser, navigate]);

    const handleUpdateProfile = async (e) => {
        e.preventDefault();

        if (!displayName.trim()) {
            setError("Le nom d'affichage est requis.");
            return;
        }

        try {
            setIsUpdating(true);

            // Mettre à jour le profil dans Firebase Authentication
            await updateProfile(currentUser, {
                displayName: displayName
            });

            // Mettre à jour le profil dans la Realtime Database
            const userRef = ref(database, `users/${currentUser.uid}/profile`);
            await update(userRef, {
                displayName: displayName,
                lastUpdated: serverTimestamp()
            });

            setSuccess("Profil mis à jour avec succès !");
            setTimeout(() => setSuccess(''), 3000);

            setIsUpdating(false);
        } catch (error) {
            console.error("Erreur lors de la mise à jour du profil:", error);
            setError("Une erreur s'est produite lors de la mise à jour du profil.");
            setIsUpdating(false);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
        } catch (error) {
            console.error("Erreur lors de la déconnexion:", error);
            setError("Une erreur s'est produite lors de la déconnexion.");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-purple-100">
            <header className="bg-indigo-700 shadow-lg">
                <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
                    <h1 className="text-2xl font-bold text-white">Mon Profil</h1>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="text-white hover:text-indigo-200 transition-colors"
                        >
                            Retour
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                {error && (
                    <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24"
                                     stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">
                                    {error}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {success && (
                    <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24"
                                     stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                          d="M5 13l4 4L19 7"/>
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-green-700">
                                    {success}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-2">
                        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                            <div className="px-4 py-5 sm:px-6 bg-indigo-50">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                    Informations personnelles
                                </h3>
                                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                    Modifiez votre profil utilisateur
                                </p>
                            </div>
                            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                                <form onSubmit={handleUpdateProfile}>
                                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                        <div className="sm:col-span-6">
                                            <label htmlFor="display-name"
                                                   className="block text-sm font-medium text-gray-700">
                                                Nom d'affichage
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    type="text"
                                                    name="display-name"
                                                    id="display-name"
                                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                    value={displayName}
                                                    onChange={(e) => setDisplayName(e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="sm:col-span-6">
                                            <label htmlFor="phone-number"
                                                   className="block text-sm font-medium text-gray-700">
                                                Téléphone
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    type="text"
                                                    name="phone-number"
                                                    id="phone-number"
                                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                    value={phoneNumber}
                                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="sm:col-span-6">
                                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                                Adresse e-mail
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    type="email"
                                                    name="email"
                                                    id="email"
                                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50"
                                                    value={email}
                                                    disabled
                                                />
                                            </div>
                                            <p className="mt-2 text-sm text-gray-500">
                                                L'adresse e-mail ne peut pas être modifiée directement.
                                            </p>
                                        </div>

                                        <div className="sm:col-span-6">
                                            <div className="flex items-center">
                                                <svg className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24"
                                                     stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                                                </svg>
                                                <span className="ml-2 text-sm text-gray-700">
                          Rôle: {isAdmin ? 'Administrateur' : 'Joueur'}
                        </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-6 flex justify-end">
                                        <button
                                            type="submit"
                                            disabled={isUpdating}
                                            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                                        >
                                            {isUpdating ? 'Mise à jour...' : 'Enregistrer'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
                            <div className="px-4 py-5 sm:px-6 bg-indigo-50">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                    Actions du compte
                                </h3>
                            </div>
                            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                                <button
                                    onClick={handleLogout}
                                    className="w-full sm:w-auto flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                    Se déconnecter
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-1">
                        <div className="bg-white shadow sm:rounded-lg mb-6">
                            <div className="px-4 py-5 sm:px-6 bg-indigo-50">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                    Statistiques
                                </h3>
                                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                    Votre activité sur BibleQuiz
                                </p>
                            </div>
                            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                                <dl className="space-y-4">
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">
                                            Quiz complétés
                                        </dt>
                                        <dd className="mt-1 text-xl font-semibold text-gray-900">
                                            {userStats?.totalGames || 0}
                                        </dd>
                                    </div>

                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">
                                            Score total
                                        </dt>
                                        <dd className="mt-1 text-xl font-semibold text-gray-900">
                                            {userStats?.totalScore || 0} points
                                        </dd>
                                    </div>

                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">
                                            Questions d'entraînement répondues
                                        </dt>
                                        <dd className="mt-1 text-xl font-semibold text-gray-900">
                                            {userStats?.questionsAnswered || 0}
                                        </dd>
                                    </div>

                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">
                                            Réponses correctes (entraînement)
                                        </dt>
                                        <dd className="mt-1 text-xl font-semibold text-gray-900">
                                            {userStats?.correctAnswers || 0}
                                        </dd>
                                    </div>

                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">
                                            Précision (entraînement)
                                        </dt>
                                        <dd className="mt-1 text-xl font-semibold text-gray-900">
                                            {userStats?.questionsAnswered > 0
                                                ? Math.round((userStats.correctAnswers / userStats.questionsAnswered) * 100)
                                                : 0}%
                                        </dd>
                                    </div>

                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">
                                            Dernière activité
                                        </dt>
                                        <dd className="mt-1 text-xl font-semibold text-gray-900">
                                            {userStats?.lastPlayed
                                                ? new Date(userStats.lastPlayed).toLocaleDateString()
                                                : 'Jamais'}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        <div className="bg-white shadow sm:rounded-lg">
                            <div className="px-4 py-5 sm:px-6 bg-indigo-50">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                    Navigation rapide
                                </h3>
                            </div>
                            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                                <nav className="space-y-2">
                                    <button
                                        onClick={() => navigate('/dashboard')}
                                        className="w-full text-left px-3 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-indigo-50 hover:text-indigo-700"
                                    >
                                        Tableau de bord
                                    </button>
                                    <button
                                        onClick={() => navigate('/training')}
                                        className="w-full text-left px-3 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-indigo-50 hover:text-indigo-700"
                                    >
                                        Mode entraînement
                                    </button>
                                    {isAdmin && (
                                        <button
                                            onClick={() => navigate('/admin/dashboard')}
                                            className="w-full text-left px-3 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-indigo-50 hover:text-indigo-700"
                                        >
                                            Administration
                                        </button>
                                    )}
                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
